import { AxiosPromise } from 'axios';
import { Resp } from '@/model/Resp';
import { commonApi } from '@/services/api';
import {
  CartRecommendQualification,
  DEFAULT_CART_RECOMMEND_ITEMS,
  CART_RECOMMEND_TARGET_SCENE,
  RecommendItem,
} from '@/views/wheelOfFortune/components/recommend/types';
import { isEnableCartRecommendMvp, isCartRecommendMockEnabled } from '@/utils/experiment';
import { getCompanyInfo } from '@/utils/getCompanyInfo';

const QUALIFICATION_ACTION = 'cartsee.form.cartRecommend.qualification';
const DISMISS_ACTION = 'cartsee.form.cartRecommend.dismiss';

function buildMockQualification(): CartRecommendQualification {
  return {
    eligible: true,
    recommendationId: 'cart_recommend_mvp_mock',
    targetScene: CART_RECOMMEND_TARGET_SCENE,
    experimentGroup: 'mock',
    items: DEFAULT_CART_RECOMMEND_ITEMS,
  };
}

function normalizeRecommendItem(raw: any, index: number): RecommendItem {
  const fallback = DEFAULT_CART_RECOMMEND_ITEMS[0];
  const titlePrefix = raw?.titlePrefix || fallback.titlePrefix;
  const titleHighlight = raw?.titleHighlight || fallback.titleHighlight;
  const legacyTitle = raw?.title as string | undefined;

  return {
    id: raw?.id || `cart_recommend_${index + 1}`,
    scene: raw?.scene ?? CART_RECOMMEND_TARGET_SCENE,
    badge: raw?.badge || fallback.badge,
    titlePrefix: legacyTitle && !raw?.titlePrefix ? legacyTitle.replace(fallback.titleHighlight, '') : titlePrefix,
    titleHighlight: raw?.titleHighlight || (legacyTitle?.includes('【') ? legacyTitle.slice(legacyTitle.indexOf('【')) : fallback.titleHighlight),
    descriptionLines: Array.isArray(raw?.descriptionLines) && raw.descriptionLines.length
      ? raw.descriptionLines
      : fallback.descriptionLines,
    primaryCta: raw?.primaryCta || fallback.primaryCta,
    secondaryCta: raw?.secondaryCta || fallback.secondaryCta,
  };
}

function normalizeQualification(data: any): CartRecommendQualification {
  const eligible = Boolean(data?.eligible);
  const rawItems = Array.isArray(data?.items) ? data.items : [];
  return {
    eligible,
    recommendationId: data?.recommendationId,
    targetScene: data?.targetScene ?? CART_RECOMMEND_TARGET_SCENE,
    experimentGroup: data?.experimentGroup,
    items: rawItems.length
      ? rawItems.map(normalizeRecommendItem)
      : DEFAULT_CART_RECOMMEND_ITEMS,
  };
}

export async function fetchCartRecommendQualification(
  showLoading = false
): Promise<CartRecommendQualification> {
  if (!isEnableCartRecommendMvp()) {
    return { eligible: false };
  }

  const companyId = getCompanyInfo()?.id;
  const params = companyId ? { companyId } : {};

  try {
    const response = await commonApi(params, QUALIFICATION_ACTION, showLoading);
    if (response?.status === 200 && response?.data) {
      if (response.data.code != null && response.data.code !== 0) {
        throw new Error(response.data.message || 'qualification failed');
      }
      const payload = response.data.data ?? response.data;
      return normalizeQualification(payload);
    }
  } catch (error) {
    if (!isCartRecommendMockEnabled()) {
      return { eligible: false };
    }
  }

  if (isCartRecommendMockEnabled()) {
    return buildMockQualification();
  }

  return { eligible: false };
}

export function dismissCartRecommend(
  params: { recommendationId?: string; targetScene?: number; companyId?: number },
  showLoading = false
): AxiosPromise<Resp<unknown>> {
  return commonApi(
    {
      ...params,
      targetScene: params.targetScene ?? CART_RECOMMEND_TARGET_SCENE,
    },
    DISMISS_ACTION,
    showLoading
  );
}
